import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Shield, ShieldCheck, ShieldAlert, ShieldOff, Loader2, AlertTriangle, Clock, CheckCircle, XCircle, History } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMULES = {
  'appartement-1': 'Apt. Terrasse',
  'appartement-2': 'Apt. Panorama',
  'gite-seul': 'Gite seul',
  'gite-salle': 'Gite + Salle',
};

const STATUS_CONFIG = {
  pending: { label: 'En attente', icon: Clock, className: 'bg-gray-100 text-gray-700 border-gray-200' },
  authorized: { label: 'Pre-autorisee', icon: ShieldCheck, className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  captured: { label: 'Capturee', icon: ShieldAlert, className: 'bg-red-100 text-red-700 border-red-200' },
  partially_captured: { label: 'Capturee partiellement', icon: ShieldAlert, className: 'bg-orange-100 text-orange-700 border-orange-200' },
  released: { label: 'Liberee', icon: ShieldOff, className: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const EUR = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v);

export default function AdminCautions() {
  const navigate = useNavigate();
  const [cautions, setCautions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [captureDialog, setCaptureDialog] = useState(null);
  const [captureAmount, setCaptureAmount] = useState('');
  const [captureReason, setCaptureReason] = useState('');
  const [releaseDialog, setReleaseDialog] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [showEvents, setShowEvents] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [autoReleaseLoading, setAutoReleaseLoading] = useState(false);

  const token = localStorage.getItem('admin_token');
  const api = useCallback((method, url, data) =>
    axios({ method, url: `${API}${url}`, data, headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [cautionsRes, eventsRes] = await Promise.all([
        api('get', '/api/admin/cautions'),
        api('get', '/api/admin/cautions/events'),
      ]);
      setCautions(cautionsRes.data);
      setEvents(eventsRes.data);
    } catch {
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchData();
  }, [token, navigate, fetchData]);

  const filtered = cautions.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['authorized', 'pending'].includes(c.caution_status);
    return c.caution_status === filter;
  });

  const handleAuthorize = async (id) => {
    setActionLoading(id);
    setError('');
    try {
      const res = await api('post', `/api/admin/cautions/${id}/authorize`);
      setSuccessMsg(res.data.message);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCapture = async () => {
    if (!captureDialog) return;
    setActionLoading(captureDialog.id);
    setError('');
    try {
      const amount = captureAmount ? parseFloat(captureAmount) : undefined;
      const res = await api('post', `/api/admin/cautions/${captureDialog.id}/capture`, {
        amount,
        reason: captureReason,
      });
      setSuccessMsg(res.data.message);
      setCaptureDialog(null);
      setCaptureAmount('');
      setCaptureReason('');
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la capture');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRelease = async () => {
    if (!releaseDialog) return;
    setActionLoading(releaseDialog.id);
    setError('');
    try {
      const res = await api('post', `/api/admin/cautions/${releaseDialog.id}/release`, {
        reason: releaseReason,
      });
      setSuccessMsg(res.data.message);
      setReleaseDialog(null);
      setReleaseReason('');
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la liberation');
    } finally {
      setActionLoading(null);
    }
  };

  const openDetails = async (reservationId) => {
    setDetailDialog(reservationId);
    try {
      const res = await api('get', `/api/admin/cautions/${reservationId}/details`);
      setDetailData(res.data);
    } catch {
      setDetailData(null);
    }
  };

  const handleAutoRelease = async () => {
    setAutoReleaseLoading(true);
    setError('');
    try {
      const res = await api('post', '/api/admin/cautions/auto-release');
      setSuccessMsg(res.data.message);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally {
      setAutoReleaseLoading(false);
    }
  };

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const counts = {
    all: cautions.length,
    active: cautions.filter(c => ['authorized', 'pending'].includes(c.caution_status)).length,
    authorized: cautions.filter(c => c.caution_status === 'authorized').length,
    captured: cautions.filter(c => ['captured', 'partially_captured'].includes(c.caution_status)).length,
    released: cautions.filter(c => c.caution_status === 'released').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C3E30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="cautions-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C4A962]" />
              <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Gestion des Cautions</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="cautions-auto-release-btn"
              variant="ghost"
              size="sm"
              disabled={autoReleaseLoading}
              className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-[#F2F0E9]/10"
              onClick={handleAutoRelease}
            >
              {autoReleaseLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
              Verifier auto-liberation
            </Button>
            <Button
              data-testid="cautions-events-btn"
              variant="ghost"
              size="sm"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              onClick={() => setShowEvents(true)}
            >
              <History className="w-4 h-4 mr-1" /> Historique
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Notifications */}
        {successMsg && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2" data-testid="caution-success-msg">
            <CheckCircle className="w-4 h-4" /> {successMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2" data-testid="caution-error-msg">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Auto-release policy info */}
        <div className="mb-4 p-3 rounded-lg bg-[#2C3E30]/5 border border-[#2C3E30]/10 text-sm flex items-start gap-2" data-testid="auto-release-info">
          <Clock className="w-4 h-4 text-[#2C3E30] mt-0.5 flex-shrink-0" />
          <p className="text-[#2C3E30]">
            <strong>Liberation automatique :</strong> Les pre-autorisations non capturees sont automatiquement liberees <strong>7 jours apres le depart</strong> du client. Vous disposez de ce delai pour inspecter les lieux et capturer la caution si necessaire.
          </p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { key: 'all', label: 'Total', color: 'text-[#2C3E30]', bg: 'bg-white' },
            { key: 'active', label: 'Actives', color: 'text-amber-700', bg: 'bg-amber-50' },
            { key: 'authorized', label: 'Pre-autorisees', color: 'text-emerald-700', bg: 'bg-emerald-50' },
            { key: 'captured', label: 'Capturees', color: 'text-red-700', bg: 'bg-red-50' },
            { key: 'released', label: 'Liberees', color: 'text-blue-700', bg: 'bg-blue-50' },
          ].map(s => (
            <button
              key={s.key}
              data-testid={`caution-filter-${s.key}`}
              onClick={() => setFilter(s.key)}
              className={`p-4 rounded-xl border transition-all ${filter === s.key ? 'ring-2 ring-[#2C3E30] shadow-md' : 'shadow-sm hover:shadow-md'} ${s.bg}`}
            >
              <p className={`text-2xl font-bold ${s.color}`}>{counts[s.key]}</p>
              <p className="text-xs text-[#595959] mt-1">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0]">
                  <TableHead className="text-[#2C3E30] font-medium">Client</TableHead>
                  <TableHead className="text-[#2C3E30] font-medium">Formule</TableHead>
                  <TableHead className="text-[#2C3E30] font-medium">Dates</TableHead>
                  <TableHead className="text-[#2C3E30] font-medium">Reservation</TableHead>
                  <TableHead className="text-[#2C3E30] font-medium text-right">Caution</TableHead>
                  <TableHead className="text-[#2C3E30] font-medium">Statut</TableHead>
                  <TableHead className="text-[#2C3E30] font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-[#595959]">
                      Aucune caution trouvee pour ce filtre
                    </TableCell>
                  </TableRow>
                ) : filtered.map(c => {
                  const cfg = STATUS_CONFIG[c.caution_status] || STATUS_CONFIG.pending;
                  const StatusIcon = cfg.icon;
                  return (
                    <TableRow key={c.id} className="hover:bg-[#F5F5F0]/50">
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-[#2C3E30]">{c.name}</p>
                          <p className="text-xs text-[#595959]">{c.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{FORMULES[c.accommodation_type] || c.accommodation_type}</TableCell>
                      <TableCell className="text-sm">{c.arrival_date} → {c.departure_date}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs border ${c.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : c.status === 'cancelled' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {c.status === 'confirmed' ? 'Confirmee' : c.status === 'cancelled' ? 'Annulee' : 'En attente'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">{EUR(c.caution_amount)}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs border inline-flex items-center gap-1 ${cfg.className}`}>
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </Badge>
                        {c.caution_captured_amount > 0 && (
                          <p className="text-xs text-red-600 mt-1">Capture: {EUR(c.caution_captured_amount)}</p>
                        )}
                        {c.caution_status === 'authorized' && c.days_until_auto_release != null && (
                          <p className={`text-xs mt-1 ${c.days_until_auto_release <= 2 ? 'text-red-500 font-medium' : c.days_until_auto_release <= 4 ? 'text-amber-600' : 'text-[#595959]'}`}>
                            {c.days_until_auto_release <= 0
                              ? 'Liberation imminente'
                              : `Auto-liberation dans ${c.days_until_auto_release}j`}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Authorize button */}
                          {c.caution_status === 'pending' && c.status === 'confirmed' && (
                            <Button
                              data-testid={`authorize-caution-${c.id}`}
                              size="sm"
                              variant="outline"
                              disabled={actionLoading === c.id}
                              onClick={() => handleAuthorize(c.id)}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs"
                            >
                              {actionLoading === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                              Autoriser
                            </Button>
                          )}
                          {/* Capture button */}
                          {c.caution_status === 'authorized' && (
                            <Button
                              data-testid={`capture-caution-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => { setCaptureDialog(c); setCaptureAmount(String(c.caution_amount)); }}
                              className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                            >
                              <ShieldAlert className="w-3 h-3 mr-1" /> Capturer
                            </Button>
                          )}
                          {/* Release button */}
                          {c.caution_status === 'authorized' && (
                            <Button
                              data-testid={`release-caution-${c.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => setReleaseDialog(c)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs"
                            >
                              <ShieldOff className="w-3 h-3 mr-1" /> Liberer
                            </Button>
                          )}
                          {/* Details button */}
                          <Button
                            data-testid={`details-caution-${c.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetails(c.id)}
                            className="text-[#595959] hover:text-[#2C3E30] text-xs"
                          >
                            Details
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Capture Dialog */}
      <Dialog open={!!captureDialog} onOpenChange={(o) => { if (!o) setCaptureDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Capturer la caution</DialogTitle>
            <DialogDescription>
              Capturer tout ou partie de la caution de <strong>{captureDialog?.name}</strong> ({EUR(captureDialog?.caution_amount || 0)})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Montant a capturer (EUR)</label>
              <Input
                data-testid="capture-amount-input"
                type="number"
                min="0.01"
                max={captureDialog?.caution_amount}
                step="0.01"
                value={captureAmount}
                onChange={(e) => setCaptureAmount(e.target.value)}
                className="border-[#E5E5E0]"
              />
              <p className="text-xs text-[#595959] mt-1">Maximum: {EUR(captureDialog?.caution_amount || 0)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Motif de la capture</label>
              <Input
                data-testid="capture-reason-input"
                value={captureReason}
                onChange={(e) => setCaptureReason(e.target.value)}
                placeholder="Ex: Degradation du mobilier, nettoyage supplementaire..."
                className="border-[#E5E5E0]"
              />
            </div>
            {parseFloat(captureAmount) < (captureDialog?.caution_amount || 0) && captureAmount && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
                Capture partielle: {EUR(parseFloat(captureAmount))} sur {EUR(captureDialog?.caution_amount || 0)}. Le reste sera automatiquement libere.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaptureDialog(null)} className="border-[#E5E5E0]">
              Annuler
            </Button>
            <Button
              data-testid="confirm-capture-btn"
              disabled={!captureAmount || parseFloat(captureAmount) <= 0 || actionLoading}
              onClick={handleCapture}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Capturer {captureAmount ? EUR(parseFloat(captureAmount)) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release Dialog */}
      <Dialog open={!!releaseDialog} onOpenChange={(o) => { if (!o) setReleaseDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Liberer la caution</DialogTitle>
            <DialogDescription>
              Liberer la pre-autorisation de {EUR(releaseDialog?.caution_amount || 0)} pour <strong>{releaseDialog?.name}</strong>. Aucun montant ne sera preleve.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-[#2C3E30] block mb-1">Motif (optionnel)</label>
            <Input
              data-testid="release-reason-input"
              value={releaseReason}
              onChange={(e) => setReleaseReason(e.target.value)}
              placeholder="Ex: Etat des lieux OK, aucune degradation"
              className="border-[#E5E5E0]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialog(null)} className="border-[#E5E5E0]">
              Annuler
            </Button>
            <Button
              data-testid="confirm-release-btn"
              disabled={actionLoading}
              onClick={handleRelease}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Liberer la caution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={(o) => { if (!o) { setDetailDialog(null); setDetailData(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Details de la caution</DialogTitle>
          </DialogHeader>
          {detailData ? (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-[#F5F5F0]">
                  <p className="text-xs text-[#595959]">Client</p>
                  <p className="text-sm font-medium">{detailData.name}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#F5F5F0]">
                  <p className="text-xs text-[#595959]">Montant caution</p>
                  <p className="text-sm font-medium">{EUR(detailData.caution_amount)}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#F5F5F0]">
                  <p className="text-xs text-[#595959]">Statut</p>
                  <p className="text-sm font-medium">{STATUS_CONFIG[detailData.caution_status]?.label || detailData.caution_status}</p>
                </div>
                {detailData.caution_captured_amount > 0 && (
                  <div className="p-3 rounded-lg bg-red-50">
                    <p className="text-xs text-red-600">Montant capture</p>
                    <p className="text-sm font-medium text-red-700">{EUR(detailData.caution_captured_amount)}</p>
                  </div>
                )}
              </div>
              {detailData.caution_capture_reason && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-600 mb-1">Motif de capture</p>
                  <p className="text-sm">{detailData.caution_capture_reason}</p>
                </div>
              )}
              {detailData.stripe_details && !detailData.stripe_details.error && (
                <div className="p-3 rounded-lg bg-[#F5F5F0] text-xs space-y-1">
                  <p className="font-medium text-[#2C3E30] text-sm mb-2">Stripe</p>
                  <p>ID: <span className="font-mono text-[#595959]">{detailData.stripe_details.id}</span></p>
                  <p>Statut: {detailData.stripe_details.status}</p>
                  <p>Montant: {EUR(detailData.stripe_details.amount)}</p>
                  <p>Capturable: {EUR(detailData.stripe_details.amount_capturable)}</p>
                  <p>Encaisse: {EUR(detailData.stripe_details.amount_received)}</p>
                  <p>Cree le: {detailData.stripe_details.created}</p>
                </div>
              )}
              {detailData.events && detailData.events.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-[#2C3E30] mb-2">Evenements</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {detailData.events.map(ev => (
                      <div key={ev.id} className="flex items-start gap-3 p-2 rounded-lg bg-[#F5F5F0] text-xs">
                        <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${ev.event_type === 'authorized' ? 'bg-emerald-500' : ev.event_type === 'captured' ? 'bg-red-500' : ev.event_type === 'released' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                        <div>
                          <p className="font-medium">{ev.event_type}{ev.amount > 0 ? ` - ${EUR(ev.amount)}` : ''}</p>
                          {ev.details?.reason && <p className="text-[#595959]">{ev.details.reason}</p>}
                          <p className="text-[#595959]">{new Date(ev.created_at).toLocaleString('fr-FR')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#2C3E30]" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Events History Dialog */}
      <Dialog open={showEvents} onOpenChange={setShowEvents}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Historique des evenements caution</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {events.length === 0 ? (
              <p className="text-sm text-[#595959] text-center py-8">Aucun evenement enregistre</p>
            ) : events.map(ev => {
              const res = cautions.find(c => c.id === ev.reservation_id);
              return (
                <div key={ev.id} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F5F0] text-sm">
                  <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${ev.event_type === 'authorized' ? 'bg-emerald-500' : ev.event_type === 'captured' ? 'bg-red-500' : ev.event_type === 'released' ? 'bg-blue-500' : ev.event_type.includes('failed') ? 'bg-amber-500' : 'bg-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{ev.event_type}</span>
                      {ev.amount > 0 && <span className="text-xs bg-white px-2 py-0.5 rounded border">{EUR(ev.amount)}</span>}
                    </div>
                    {res && <p className="text-xs text-[#595959]">{res.name} - {FORMULES[res.accommodation_type] || res.accommodation_type}</p>}
                    {ev.details?.reason && <p className="text-xs text-[#595959] mt-0.5">{ev.details.reason}</p>}
                    {ev.details?.error && <p className="text-xs text-red-600 mt-0.5">{ev.details.error}</p>}
                    <p className="text-xs text-[#8A8A8A] mt-1">{new Date(ev.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
